import { useEffect, useState } from 'react'
import { Alert, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import formatAmount from '../../utils/formatMoney'
import { fetchRemainder, refundRemainder } from './api'
import { destinationNote, driverNote, problemOf, remainderOutcome } from './utils'

const MUTED = 'text-gray-500 dark:text-gray-400'

const RETRY_NOTE =
  'Este restante ya se pidió y quedó sin confirmar. Primero se revisa Mercado Pago: si ya salió, sólo se cierra; si no, se pide de nuevo sin riesgo de devolverlo dos veces.'

function DriverOption({ value, checked, disabled, onChange, title, hint }) {
  const hintId = `refund-remainder-${value}-hint`
  return (
    <label
      className={`flex items-start gap-3 rounded-xl px-3.5 py-3 ring-1 transition-colors cursor-pointer ${
        checked
          ? 'ring-coral/60 bg-coral/[0.06] dark:bg-red-500/10 dark:ring-coral/50'
          : 'ring-warm_border dark:ring-dk-border hover:bg-ink/[0.03] dark:hover:bg-dk-hover'
      }`}
    >
      <input
        type='radio'
        name='refund-remainder-driver'
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        aria-describedby={hintId}
        className='mt-1 accent-coral'
      />
      <span className='flex flex-col gap-0.5'>
        <span className='text-sm font-semibold text-espresso dark:text-gray-100'>{title}</span>
        <span id={hintId} className='text-xs text-warm_gray dark:text-gray-400'>
          {hint}
        </span>
      </span>
    </label>
  )
}

export default function RefundRemainderDialog({ open, transactionId, details = [], onClose, onRefunded }) {
  const [preview, setPreview] = useState(null)
  const [loadProblem, setLoadProblem] = useState(null)
  const [driverShare, setDriverShare] = useState(null)
  const [sending, setSending] = useState(false)
  const [outcome, setOutcome] = useState(null)

  useEffect(() => {
    if (!open) return undefined
    let active = true
    setPreview(null)
    setLoadProblem(null)
    setDriverShare(null)
    setOutcome(null)
    fetchRemainder(transactionId).then(response => {
      if (!active) return
      if (response.ok) setPreview(response.data)
      else setLoadProblem(problemOf(response))
    })
    return () => {
      active = false
    }
  }, [open, transactionId])

  const close = () => {
    if (!sending) onClose()
  }

  const submit = async () => {
    setSending(true)
    setOutcome(null)
    const response = await refundRemainder(transactionId, driverShare)
    const next = remainderOutcome(response, { driverShare, currency: preview.currency })
    setSending(false)
    setOutcome(next)
    if (next.done) {
      onRefunded(next.update)
      return
    }
    if (next.driver) {
      setPreview(current => ({ ...current, driver: next.driver }))
      setDriverShare(null)
    }
  }

  const finished = Boolean(outcome?.done)
  const returned = Boolean(outcome?.update?.refund)
  const driver = preview?.driver
  const choiceRequired = Boolean(driver?.choice_required)
  const driverAmount = preview ? formatAmount(driver?.amount, preview.currency) : null
  const canSubmit = Boolean(preview) && !sending && (!choiceRequired || Boolean(driverShare))

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth='sm'>
      <DialogTitle>Reembolsar restante</DialogTitle>
      <DialogContent>
        <div className='flex flex-col gap-4 pt-1'>
          <dl className='grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm'>
            <dt className={MUTED}>ID</dt>
            <dd className='font-mono break-all'>{transactionId}</dd>
            {details.map(([label, value]) => (
              <div key={label} className='contents'>
                <dt className={MUTED}>{label}</dt>
                <dd className='break-words'>{value || '-'}</dd>
              </div>
            ))}
            {preview && (
              <>
                <dt className={MUTED}>{returned ? 'Restante devuelto' : 'A devolver'}</dt>
                <dd className='font-semibold tabular-nums'>{formatAmount(preview.amount, preview.currency)}</dd>
              </>
            )}
          </dl>

          {!preview && !loadProblem && (
            <div className='flex justify-center py-2'>
              <CircularProgress size={24} aria-label='Cargando el restante' />
            </div>
          )}

          {loadProblem && <Alert severity='error'>{loadProblem}</Alert>}

          {preview && !finished && (
            <>
              <p className={`text-sm ${MUTED}`}>{preview.in_progress ? RETRY_NOTE : destinationNote(preview)}</p>

              {!preview.in_progress && (
                <p className='text-sm text-espresso dark:text-gray-200'>{driverNote(driver, preview.currency)}</p>
              )}

              {choiceRequired && (
                <fieldset className='flex flex-col gap-2.5'>
                  <legend className='mb-2.5 text-[11px] uppercase tracking-wider font-bold text-warm_gray dark:text-gray-400'>
                    ¿Quién pone esa plata?
                  </legend>
                  <DriverOption
                    value='deduct'
                    checked={driverShare === 'deduct'}
                    disabled={sending}
                    onChange={setDriverShare}
                    title='Descontárselo al conductor'
                    hint={`Deja de cobrar esos ${driverAmount}.`}
                  />
                  <DriverOption
                    value='viatik'
                    checked={driverShare === 'viatik'}
                    disabled={sending}
                    onChange={setDriverShare}
                    title='Lo pone Viatik'
                    hint={`El conductor cobra sus ${driverAmount} igual.`}
                  />
                </fieldset>
              )}
            </>
          )}

          {outcome && <Alert severity={outcome.severity}>{outcome.message}</Alert>}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {finished || loadProblem ? (
          <button
            className='px-3 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 transition-opacity'
            onClick={close}
          >
            {finished ? 'Listo' : 'Cerrar'}
          </button>
        ) : (
          <>
            <button
              className='px-3 py-2 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-dk-hover disabled:opacity-50'
              onClick={close}
              disabled={sending}
            >
              Cancelar
            </button>
            <button
              className='px-3 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50'
              onClick={submit}
              disabled={!canSubmit}
            >
              {sending ? 'Devolviendo…' : preview?.in_progress ? 'Reintentar' : 'Devolver restante'}
            </button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
